import React, { useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  InputAdornment,
  Alert,
} from '@mui/material';
import { PersonOutline } from '@mui/icons-material';
import { api } from 'api/api';

interface ForgotPasswordProps {
  onBackToLogin: () => void;
}

export const ForgotPassword: React.FC<ForgotPasswordProps> = ({
  onBackToLogin,
}): React.ReactElement => {
  const [email, setEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const onSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await api.post('Account/ForgotPassword', {
        Email: email,
      });
      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'If an account exists for that email, a password reset link has been sent.',
        });
      } else {
        setMessage({
          type: 'error',
          text: response.data.msg || 'Something went wrong. Please try again.',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'An error occurred. Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" fontWeight={600} mb={2}>
        Reset Password
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {message ? (
        <Box>
          <Alert severity={message.type} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
          <Button
            fullWidth
            variant="contained"
            onClick={onBackToLogin}
            sx={{
              backgroundColor: '#009944',
              '&:hover': { backgroundColor: '#007c36' },
            }}
          >
            Back to Login
          </Button>
        </Box>
      ) : (
        <form onSubmit={onSubmit}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Enter your email address and we&apos;ll send you a link to reset
            your password.
          </Typography>
          <TextField
            label="Email"
            type="email"
            value={email}
            fullWidth
            margin="normal"
            required
            disabled={loading}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutline />
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            size="large"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              mt: 2,
              backgroundColor: '#009944',
              color: '#fff',
              fontWeight: 500,
              borderRadius: '4px',
              fontSize: '16px',
              '&:hover': {
                backgroundColor: '#007c36',
              },
            }}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </Button>

          <Button
            fullWidth
            variant="text"
            onClick={onBackToLogin}
            disabled={loading}
            sx={{ mt: 1, color: 'text.secondary', textTransform: 'none' }}
          >
            Back to Login
          </Button>
        </form>
      )}
    </Box>
  );
};
